import {KeyboardEvent, RefObject, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Box, Button, Flex, HStack, IconButton, Text, Textarea} from '@chakra-ui/react';
import {slimScrollbar} from '../ui/scrollbar';
import {Reply, SendHorizontal, SquareDashedMousePointer, SquareDashedX, X} from 'lucide-react';

interface CommentComposerProps {
    textareaRef: RefObject<HTMLTextAreaElement | null>;
    /** Display name of the thread being replied to; undefined means a new top-level comment. */
    replyToName: string | undefined;
    isSubmitting: boolean;
    onCancelReply: () => void;
    /** Resolves true when the text was saved and the input can be cleared. */
    onSubmit: (content: string) => Promise<boolean>;
    /** Sends one of the quick comments as it is, leaving the typed text alone. */
    onQuickComment: (content: string) => void;
    /** The button that marks a region of the picture for the comment; none without it. */
    draw?: DrawButtonState;
}

export interface DrawButtonState {
    /** Waiting for the rectangle to be dragged on the picture. */
    drawing: boolean;
    /** A region is marked for the comment. */
    attached: boolean;
    /** Why it can't be used now (e.g. replies have no region), if so. */
    disabledReason?: string;
    onClick: () => void;
}

/** One-click comments on the usual problems of a photo, each with a swatch of what it means. */
const QUICK_COMMENTS: { text: string; swatch: string }[] = [
    {text: 'kék', swatch: 'blue.400'},
    {text: 'sárga', swatch: 'yellow.400'},
    {text: 'zöld', swatch: 'green.400'},
    {text: 'lila', swatch: 'purple.400'},
    {text: 'sötét', swatch: 'gray.900'},
    {text: 'fakó', swatch: 'gray.400'},
];

const MAX_TEXTAREA_HEIGHT = 140;
/** Between the buttons, in px (`gap={1}`). */
const BUTTON_GAP = 4;

export function CommentComposer({
    textareaRef,
    replyToName,
    isSubmitting,
    onCancelReply,
    onSubmit,
    onQuickComment,
    draw,
}: CommentComposerProps) {
    const [text, setText] = useState('');
    // The draw button sits above the send button once the text takes more than a line beside both.
    const [stacked, setStacked] = useState(false);
    const drawButtonRef = useRef<HTMLButtonElement>(null);
    const quickCommentsRef = useRef<HTMLDivElement>(null);
    const canSubmit = text.trim().length > 0 && !isSubmitting;
    const hasDraw = !!draw;

    // Grow with the content up to a cap, then scroll.
    useLayoutEffect(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.style.height = 'auto';
        const drawButton = drawButtonRef.current;
        if (drawButton) {
            // Measured as wide as it is with the buttons side by side, so stacking them (which widens
            // the text) can't make it fit on a line again and flip them back.
            if (stacked) node.style.width = `${node.offsetWidth - drawButton.offsetWidth - BUTTON_GAP}px`;
            const style = getComputedStyle(node);
            const oneLine = parseFloat(style.lineHeight) + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
            setStacked(node.scrollHeight > oneLine + 1);
            node.style.width = '';
            node.style.height = 'auto';
        }
        node.style.height = `${Math.min(node.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
    }, [text, textareaRef, stacked, hasDraw]);

    // The quick comments scroll sideways without a scrollbar: a mouse wheel scrolls them too.
    useEffect(() => {
        const node = quickCommentsRef.current;
        if (!node) return;
        const onWheel = (e: WheelEvent) => {
            if (e.deltaX !== 0 || e.deltaY === 0 || node.scrollWidth <= node.clientWidth) return;
            // Not passive: the sidebar mustn't scroll along.
            e.preventDefault();
            node.scrollLeft += e.deltaY;
        };
        node.addEventListener('wheel', onWheel, {passive: false});
        return () => node.removeEventListener('wheel', onWheel);
    }, []);

    async function submit() {
        if (!canSubmit) return;
        if (await onSubmit(text.trim())) setText('');
    }

    function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
        } else if (e.key === 'Escape') {
            // Keep Escape from closing the whole viewer while typing.
            e.stopPropagation();
            if (replyToName !== undefined) onCancelReply();
            else textareaRef.current?.blur();
        }
    }

    return <Box borderTopWidth="1px" borderColor="whiteAlpha.200" bg="gray.900" px={3} pt={2} pb={3} flexShrink={0}>
        {replyToName !== undefined &&
            <HStack
                gap={1.5}
                mb={2}
                pl={2}
                pr={1}
                py={0.5}
                bg="blue.900"
                color="blue.100"
                borderRadius="md"
                fontSize="xs"
            >
                <Reply size={12}/>
                <Text flex={1} lineClamp={1}>
                    Válasz neki: <Text as="span" fontWeight="semibold">{replyToName}</Text>
                </Text>
                <IconButton
                    aria-label="Válasz megszakítása"
                    title="Válasz megszakítása"
                    size="xs"
                    minW={5}
                    h={5}
                    variant="ghost"
                    color="blue.100"
                    _hover={{bg: 'whiteAlpha.200'}}
                    onClick={onCancelReply}
                >
                    <X size={12}/>
                </IconButton>
            </HStack>
        }
        <HStack
            ref={quickCommentsRef}
            gap={1.5}
            mb={2}
            mx={-3}
            px={3}
            overflowX="auto"
            css={{scrollbarWidth: 'none', '&::-webkit-scrollbar': {display: 'none'}}}
            role="group"
            aria-label="Gyors megjegyzések"
        >
            {QUICK_COMMENTS.map(({text, swatch}) => <Button
                key={text}
                size="xs"
                h={6}
                px={2.5}
                flexShrink={0}
                borderRadius="full"
                variant="outline"
                borderColor="whiteAlpha.300"
                color="gray.100"
                _hover={{bg: 'whiteAlpha.200'}}
                disabled={isSubmitting}
                title={`„${text}” megjegyzés küldése`}
                onClick={() => onQuickComment(text)}
            >
                <Box boxSize={2} borderRadius="full" bg={swatch} borderWidth="1px" borderColor="whiteAlpha.500"/>
                {text}
            </Button>)}
        </HStack>
        <HStack
            align="flex-end"
            gap={1}
            bg="whiteAlpha.100"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="transparent"
            pl={3}
            pr={1}
            py={1}
            _focusWithin={{borderColor: 'blue.300', bg: 'whiteAlpha.50'}}
            transition="border-color 0.15s, background 0.15s"
        >
            <Textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={replyToName !== undefined ? 'Válasz írása…' : 'Új megjegyzés…'}
                aria-label={replyToName !== undefined ? 'Válasz' : 'Új megjegyzés'}
                rows={1}
                resize="none"
                unstyled
                w="full"
                borderRadius="md"
                lineHeight="short"
                px={0}
                outline="none"
                bg="transparent"
                fontSize="sm"
                color="white"
                _placeholder={{color: 'gray.500'}}
                py={1.5}
                minH="unset"
                overflowY="auto"
                css={slimScrollbar}
            />
            <Flex direction={stacked ? 'column' : 'row'} gap={1} flexShrink={0}>
                {draw &&
                    <IconButton
                        ref={drawButtonRef}
                        aria-label={draw.attached ? 'Kijelölés eltávolítása' : 'Terület kijelölése a képen'}
                        aria-pressed={draw.drawing || draw.attached}
                        title={draw.disabledReason
                            ?? (draw.drawing ? 'Kijelölés megszakítása (Esc)'
                                : draw.attached ? 'Kijelölés eltávolítása' : 'Terület kijelölése a képen')}
                        size="sm"
                        borderRadius="lg"
                        variant="ghost"
                        color={draw.drawing || draw.attached ? 'orange.300' : 'gray.400'}
                        bg={draw.drawing ? 'whiteAlpha.200' : undefined}
                        _hover={{bg: 'whiteAlpha.200', color: draw.drawing || draw.attached ? 'orange.200' : 'white'}}
                        disabled={!!draw.disabledReason}
                        onClick={draw.onClick}
                    >
                        {draw.attached && !draw.drawing ? <SquareDashedX size={16}/> : <SquareDashedMousePointer size={16}/>}
                    </IconButton>
                }
                <IconButton
                    aria-label="Küldés"
                    title="Küldés (Enter)"
                    size="sm"
                    borderRadius="lg"
                    colorPalette="blue"
                    variant={canSubmit ? 'solid' : 'ghost'}
                    color={canSubmit ? undefined : 'gray.500'}
                    disabled={!canSubmit}
                    loading={isSubmitting}
                    onClick={submit}
                >
                    <SendHorizontal size={16}/>
                </IconButton>
            </Flex>
        </HStack>
        <Text fontSize="2xs" color="gray.500" mt={1} textAlign="right">
            Enter: küldés · Shift+Enter: új sor
        </Text>
    </Box>;
}
